import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Card,
  Spinner,
  Badge,
  HStack,
  VStack,
  Divider,
  IconButton,
  Select,
} from "@chakra-ui/react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { 
  MdQrCodeScanner,
  MdTableBar,
  MdTrendingUp,
  MdCalendarToday,
  MdArrowDropDown,
  MdAccessTime,
  MdToday,
  MdArrowUpward,
  MdArrowDownward,
  MdChevronLeft,
  MdChevronRight,
} from "react-icons/md";
import { tableService } from "../../../../services/tableService";

const COLORS = ['#E45858', '#4299E1', '#48BB78', '#ED8936', '#9F7AEA'];
const ITEMS_PER_PAGE = 10;

export default function TableUsageAnalysis() {
  const [isLoading, setIsLoading] = useState(true);
  const [tableUsageData, setTableUsageData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = yüksekten düşüğe
  
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const brandColor = useColorModeValue("brand.500", "brand.400");
  const subtleText = useColorModeValue("gray.600", "gray.400");
  const bgStat = useColorModeValue("gray.50", "navy.700");
  const hoverBg = useColorModeValue("gray.100", "navy.700");

  useEffect(() => {
    fetchTableUsageData();
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    setCurrentPage(1); // Filtre değiştiğinde sayfayı sıfırla
  }, [selectedYear, selectedMonth, sortOrder]);

  const fetchTableUsageData = async () => {
    setIsLoading(true);
    try {
      const params = { year: selectedYear };
      if (selectedMonth) {
        params.month = selectedMonth;
      }
      const response = await tableService.getTableUsageAnalytics(params);
      if (response.success) {
        setTableUsageData(response.data);
      }
    } catch (error) {
      console.error("Masa kullanım analizi verileri getirilirken hata:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Sıralanmış ve sayfalanmış masa verileri
  const sortedAndPaginatedTables = useMemo(() => {
    if (!tableUsageData?.tableStats) return { sorted: [], paginated: [], totalPages: 0 };
    
    // Kullanım sayısına göre sırala
    const sorted = [...tableUsageData.tableStats].sort((a, b) => {
      if (sortOrder === 'desc') {
        return b.scanCount - a.scanCount; // Yüksekten düşüğe
      } else {
        return a.scanCount - b.scanCount; // Düşükten yükseğe
      }
    });
    
    // Pagination
    const totalPages = Math.ceil(sorted.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginated = sorted.slice(startIndex, endIndex);
    
    return { sorted, paginated, totalPages };
  }, [tableUsageData?.tableStats, sortOrder, currentPage]);

  // Masa kullanım oranlarını hesapla (pie chart için)
  const getTableUsagePercentages = () => {
    if (!tableUsageData?.tableStats || tableUsageData.tableStats.length === 0) return [];
    
    const total = tableUsageData.totalScans || 1;
    return tableUsageData.tableStats
      .slice(0, 5) // Top 5 masa
      .map(table => ({
        name: table.tableName || `Masa ${table.tableId}`,
        value: table.scanCount,
        percentage: ((table.scanCount / total) * 100).toFixed(1)
      }));
  };


  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (!tableUsageData) {
    return (
      <Card p={6} bg={cardBg}>
        <Text color={textColor} textAlign="center">
          Veri bulunamadı
        </Text>
      </Card>
    );
  }

  const tableUsagePercentages = getTableUsagePercentages();
  const avgUsage = tableUsageData.tableStats?.length > 0
    ? Math.round(tableUsageData.totalScans / tableUsageData.tableStats.length)
    : 0;

  return (
    <Box>
      {/* Üst Bar - Yıl/Ay Seçimi */}
      <Card p={4} bg={cardBg} mb="20px">
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <HStack spacing={4}>
            <Menu>
              <MenuButton 
                as={Button} 
                rightIcon={<MdArrowDropDown />}
                leftIcon={<MdCalendarToday />}
                variant="outline"
                colorScheme="brand"
              >
                {selectedYear}
              </MenuButton>
              <MenuList>
                {[2023, 2024, 2025, 2026].map(year => (
                  <MenuItem 
                    key={year} 
                    onClick={() => setSelectedYear(year)}
                    bg={selectedYear === year ? "brand.50" : "transparent"}
                  >
                    {year}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            <Menu>
              <MenuButton 
                as={Button} 
                rightIcon={<MdArrowDropDown />}
                leftIcon={<MdToday />}
                variant="outline"
                colorScheme="brand"
                isActive={selectedMonth !== null}
              >
                {selectedMonth 
                  ? [
                      "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
                      "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
                    ][selectedMonth - 1]
                  : "Tüm Aylar"}
              </MenuButton>
              <MenuList>
                <MenuItem onClick={() => setSelectedMonth(null)}>
                  Tüm Aylar
                </MenuItem>
                <Divider />
                {[
                  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
                  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
                ].map((month, index) => (
                  <MenuItem 
                    key={index + 1}
                    onClick={() => setSelectedMonth(index + 1)}
                    bg={selectedMonth === index + 1 ? "brand.50" : "transparent"}
                  >
                    {month}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </HStack>

          <Text fontSize="sm" color={subtleText}>
            {selectedMonth 
              ? `${selectedMonth}/${selectedYear} Dönemi`
              : `${selectedYear} Yılı`}
          </Text>
        </Flex>
      </Card>

      {/* İstatistik Kartları */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing="20px" mb="20px">
        <Card p={6} bg={cardBg}>
          <Stat>
            <Flex justify="space-between" align="start" mb={2}>
              <Box>
                <StatLabel color={textColor} fontSize="sm" fontWeight="600">
                  Toplam QR Okutma
                </StatLabel>
                <StatNumber color={textColor} fontSize="2xl" mt={1}>
                  {tableUsageData.totalScans?.toLocaleString() || 0}
                </StatNumber>
              </Box>
              <Box
                bg={bgStat}
                p={3}
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={MdQrCodeScanner} color={brandColor} w="24px" h="24px" />
              </Box>
            </Flex>
            <StatHelpText color={subtleText} fontSize="xs" mb={0}>
              Seçili dönem
            </StatHelpText>
          </Stat>
        </Card>

        <Card p={6} bg={cardBg}>
          <Stat>
            <Flex justify="space-between" align="start" mb={2}>
              <Box>
                <StatLabel color={textColor} fontSize="sm" fontWeight="600">
                  Aktif Masa Sayısı
                </StatLabel>
                <StatNumber color={textColor} fontSize="2xl" mt={1}>
                  {tableUsageData.tableStats?.length || 0}
                </StatNumber>
              </Box>
              <Box
                bg={bgStat}
                p={3}
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={MdTableBar} color={brandColor} w="24px" h="24px" />
              </Box>
            </Flex>
            <StatHelpText color={subtleText} fontSize="xs" mb={0}>
              QR okutulan masalar
            </StatHelpText>
          </Stat>
        </Card>

        <Card p={6} bg={cardBg}>
          <Stat>
            <Flex justify="space-between" align="start" mb={2}>
              <Box>
                <StatLabel color={textColor} fontSize="sm" fontWeight="600">
                  Ortalama Okutma
                </StatLabel>
                <StatNumber color={textColor} fontSize="2xl" mt={1}>
                  {avgUsage}
                </StatNumber>
              </Box>
              <Box
                bg={bgStat}
                p={3}
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={MdTrendingUp} color={brandColor} w="24px" h="24px" />
              </Box>
            </Flex>
            <StatHelpText color={subtleText} fontSize="xs" mb={0}>
              Masa başına ortalama
            </StatHelpText>
          </Stat>
        </Card>

        <Card p={6} bg={cardBg}>
          <Stat>
            <Flex justify="space-between" align="start" mb={2}>
              <Box>
                <StatLabel color={textColor} fontSize="sm" fontWeight="600">
                  En Yüksek Kullanım
                </StatLabel>
                <StatNumber color={textColor} fontSize="2xl" mt={1}>
                  {tableUsageData.topTables?.[0]?.scanCount || 0}
                </StatNumber>
              </Box>
              <Box
                bg={bgStat}
                p={3}
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={MdAccessTime} color={brandColor} w="24px" h="24px" />
              </Box>
            </Flex>
            <StatHelpText color={subtleText} fontSize="xs" mb={0}>
              {tableUsageData.topTables?.[0]?.tableName || "Masa yok"}
            </StatHelpText>
          </Stat>
        </Card>
      </SimpleGrid>

      {/* Grafikler - Yan Yana */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="20px" mb="20px">
        {/* Masa Kullanım Dağılımı - Pie Chart */}
        {tableUsagePercentages.length > 0 && (
          <Card p={6} bg={cardBg}>
            <Text color={textColor} fontSize="lg" fontWeight="700" mb={4}>
              Masa Kullanım Dağılımı
            </Text>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={tableUsagePercentages}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name}: %${percentage}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {tableUsagePercentages.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* En Çok Kullanılan Masalar - Bar Chart */}
        {tableUsageData.topTables && tableUsageData.topTables.length > 0 && (
          <Card p={6} bg={cardBg}>
            <Text color={textColor} fontSize="lg" fontWeight="700" mb={4}>
              En Çok Kullanılan Masalar (Top 5)
            </Text>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={tableUsageData.topTables.map((table, index) => ({
                  name: table.tableName || `Masa ${index + 1}`,
                  kullanım: table.scanCount
                }))}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <RechartsTooltip />
                <Bar dataKey="kullanım" fill="#E45858" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </SimpleGrid>

      {/* Masa Bazında Detaylı İstatistikler */}
      <Card p={6} bg={cardBg} mb="20px">
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
          <Text color={textColor} fontSize="lg" fontWeight="700">
            Masa Bazında Detaylı İstatistikler
          </Text>
          <HStack spacing={4}>
            <Badge colorScheme="brand" fontSize="sm">
              {tableUsageData.tableStats?.length || 0} Masa
            </Badge>
          </HStack>
        </Flex>
        
        <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th color={textColor}>Masa Adı</Th>
                    <Th color={textColor} isNumeric>
                      <HStack spacing={1} justify="flex-end">
                        <Text>Kullanım Sayısı</Text>
                        <Icon 
                          as={sortOrder === 'desc' ? MdArrowDownward : MdArrowUpward} 
                          fontSize="sm"
                          cursor="pointer"
                          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                        />
                      </HStack>
                    </Th>
                    <Th color={textColor} isNumeric>Kullanım Oranı</Th>
                    <Th color={textColor}>Son Kullanım</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedAndPaginatedTables.paginated.map((table, index) => {
                    const percentage = tableUsageData.totalScans > 0
                      ? ((table.scanCount / tableUsageData.totalScans) * 100).toFixed(1)
                      : 0;
                    const globalIndex = sortedAndPaginatedTables.sorted.findIndex(t => t.tableId === table.tableId);
                    
                    return (
                      <Tr key={table.tableId} _hover={{ bg: hoverBg }}>
                        <Td color={textColor}>
                          <Flex align="center">
                            <Icon as={MdTableBar} mr={2} color={brandColor} />
                            <Text fontWeight="600">
                              {table.tableName || `Masa ${table.tableId}`}
                            </Text>
                            {globalIndex < 3 && (
                              <Badge ml={2} colorScheme="red" fontSize="xs">
                                Top {globalIndex + 1}
                              </Badge>
                            )}
                          </Flex>
                        </Td>
                        <Td color={textColor} isNumeric>
                          <Badge colorScheme="red" fontSize="md" px={2} py={1}>
                            {table.scanCount}
                          </Badge>
                        </Td>
                        <Td color={textColor} isNumeric>
                          <HStack spacing={2} justify="flex-end">
                            <Text fontSize="sm" color={subtleText}>
                              %{percentage}
                            </Text>
                            <Box
                              w="60px"
                              h="8px"
                              bg="gray.200"
                              borderRadius="full"
                              overflow="hidden"
                            >
                              <Box
                                w={`${percentage}%`}
                                h="100%"
                                bg={brandColor}
                                borderRadius="full"
                              />
                            </Box>
                          </HStack>
                        </Td>
                        <Td color={textColor}>
                          {table.lastScan
                            ? new Date(table.lastScan).toLocaleDateString("tr-TR", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "-"}
                        </Td>
                      </Tr>
                    );
                  })}
                  {sortedAndPaginatedTables.paginated.length === 0 && (
                    <Tr>
                      <Td colSpan={4} color={textColor} textAlign="center" py={8}>
                        <VStack spacing={2}>
                          <Icon as={MdQrCodeScanner} w={12} h={12} color={subtleText} />
                          <Text color={subtleText}>
                            Henüz masa kullanımı verisi bulunmamaktadır
                          </Text>
                        </VStack>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {sortedAndPaginatedTables.totalPages > 1 && (
              <Flex justify="space-between" align="center" mt={4} pt={4} borderTop="1px" borderColor={borderColor}>
                <Text fontSize="sm" color={subtleText}>
                  Sayfa {currentPage} / {sortedAndPaginatedTables.totalPages} 
                  ({sortedAndPaginatedTables.sorted.length} masa)
                </Text>
                <HStack spacing={2}>
                  <IconButton
                    aria-label="Önceki sayfa"
                    icon={<MdChevronLeft />}
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    isDisabled={currentPage === 1}
                  />
                  {Array.from({ length: sortedAndPaginatedTables.totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // İlk, son ve mevcut sayfa civarındaki sayfaları göster
                      return page === 1 || 
                             page === sortedAndPaginatedTables.totalPages || 
                             Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, index, array) => {
                      // Eksik sayfalar için "..." ekle
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <Text px={2}>...</Text>}
                          <Button
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            colorScheme={currentPage === page ? "brand" : "gray"}
                            variant={currentPage === page ? "solid" : "outline"}
                          >
                            {page}
                          </Button>
                        </React.Fragment>
                      );
                    })}
                  <IconButton
                    aria-label="Sonraki sayfa"
                    icon={<MdChevronRight />}
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(sortedAndPaginatedTables.totalPages, prev + 1))}
                    isDisabled={currentPage === sortedAndPaginatedTables.totalPages}
                  />
                </HStack>
              </Flex>
            )}
      </Card>
    </Box>
  );
}
